import { Injectable, inject, signal, WritableSignal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { map, catchError, tap } from "rxjs/operators";

// Generated OpenAPI client
import { ExchangeApiService as GeneratedExchangeService } from '@api/api/exchange.service';
import { ReconciliationVenue as GeneratedReconciliationVenue } from '@api/model/reconciliationVenue';
import type { ExchangeCredentialsResponse as GeneratedExchangeCredentialsResponse } from '@api/model/exchangeCredentialsResponse';
import type { CreateCredentialsRequest as GeneratedCreateCredentialsRequest } from '@api/model/createCredentialsRequest';
import type { UpdateCredentialsRequest as GeneratedUpdateCredentialsRequest } from '@api/model/updateCredentialsRequest';
import type { ReconnectExchangeResponse as GeneratedReconnectExchangeResponse } from '@api/model/reconnectExchangeResponse';
import type { CredentialMarketsResponse as GeneratedCredentialMarketsResponse } from '@api/model/credentialMarketsResponse';
import type { CredentialStatusResponse as GeneratedCredentialStatusResponse } from '@api/model/credentialStatusResponse';
import type { CredentialSyncStatusResponse as GeneratedCredentialSyncStatusResponse } from '@api/model/credentialSyncStatusResponse';
import type { SyncResponse as GeneratedSyncResponse } from '@api/model/syncResponse';
import type { ReconciliationBindingResponse as GeneratedReconciliationBindingResponse } from '@api/model/reconciliationBindingResponse';
import type { ReconciliationBindingCandidateResponse as GeneratedReconciliationBindingCandidateResponse } from '@api/model/reconciliationBindingCandidateResponse';
import type { UpsertReconciliationBindingRequest as GeneratedUpsertReconciliationBindingRequest } from '@api/model/upsertReconciliationBindingRequest';
import type { Symbol as GeneratedSymbol } from '@api/model/symbol';

/**
 * Exchange credentials response from backend API
 * Matches src/api_server/routes/exchange.rs:ExchangeCredentialsResponse
 */
export interface ExchangeCredentialsResponse {
  id: string;
  exchange_name: string;
  exchange_account_id: string | null;
  label: string;
  subaccount: string | null;
  testnet: boolean;
  can_trade: boolean;
  can_withdraw: boolean;
  can_read: boolean;
  rate_limit_per_second: number | null;
  rate_limit_per_minute: number | null;
  last_used_at: string | null;
  rotation_required: boolean;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

/**
 * Create exchange credentials request
 * Matches src/api_server/routes/exchange.rs:CreateCredentialsRequest
 */
export interface CreateExchangeCredentialsRequest {
  exchange_name: string;
  exchange_account_id?: string;
  label: string;
  api_key: string;
  api_secret: string;
  passphrase?: string;
  subaccount?: string;
  testnet?: boolean;
  can_trade?: boolean;
  can_withdraw?: boolean;
  rate_limit_per_second?: number;
  rate_limit_per_minute?: number;
  expires_at?: string;
}

/**
 * Update exchange credentials request
 * Matches src/api_server/routes/exchange.rs:UpdateCredentialsRequest
 */
export interface UpdateExchangeCredentialsRequest {
  label?: string;
  can_trade?: boolean;
  can_withdraw?: boolean;
  can_read?: boolean;
  rate_limit_per_second?: number;
  rate_limit_per_minute?: number;
  is_active?: boolean;
}

/**
 * Connection test result
 */
export interface ConnectionTestResult {
  success: boolean;
  message: string;
  latency_ms: number;
  exchange: string;
  testnet: boolean;
  timestamp: string;
}

/**
 * Result of a per-exchange live connection health probe.
 *
 * Maps the backend `ReconnectExchangeResponse`
 * (POST /api/v1/exchanges/{exchange_name}/reconnect), which performs a health
 * check on the exchange's live connection and reports its current status. This
 * is the real connection-status API — the admin frontend does NOT receive a
 * per-exchange WebSocket feed (the backend only registers a fixed /ws/* route
 * set; there is no /ws/exchange-<name> endpoint).
 */
export interface ExchangeHealthResult {
  healthy: boolean;
  message: string;
  exchange: string;
  latencyMs: number;
  timestamp: string;
}

/**
 * Frontend exchange model for component use
 * Maps from ExchangeCredentialsResponse with additional computed fields
 */
export interface Exchange {
  id: string;
  name: string;
  label: string;
  /**
   * Live connection health of the venue.
   *
   * IMPORTANT: this is NOT derived from the credential `is_active` flag (which
   * only records whether an operator has enabled the credential). It reflects
   * the result of a real server-side health probe:
   * - `'connected'`  — a probe (test / reconnect health-check) reported healthy
   * - `'error'`      — a probe reported unhealthy / failed
   * - `'disconnected'` — the credential is disabled (`is_active === false`)
   * - `'unknown'`    — credential is active but has not yet been probed this session
   *
   * On initial list load the venue is `'unknown'`; the connection-status panel
   * probes each exchange and folds the real result into the store (see
   * {@link ExchangeStore.applyHealthProbe}).
   */
  status: "connected" | "disconnected" | "error" | "unknown";
  apiKeyMasked: string;
  subaccount: string | null;
  testnet: boolean;
  canTrade: boolean;
  canWithdraw: boolean;
  canRead: boolean;
  rateLimitPerSecond: number | null;
  rateLimitPerMinute: number | null;
  lastUsedAt: Date | null;
  rotationRequired: boolean;
  expiresAt: Date | null;
  isActive: boolean;
  createdAt: Date;
}

/**
 * Tradable markets (symbols) for a credential's exchange.
 * Mirrors backend CredentialMarketsResponse (exchange.rs GAP-016).
 * The `symbols` field uses the generated Symbol type directly.
 */
export interface CredentialMarketsResult {
  credentialId: string;
  exchangeName: string;
  symbols: GeneratedSymbol[];
  count: number;
}

/**
 * Connectivity/validity status for a credential's exchange.
 * Mirrors backend CredentialStatusResponse (exchange.rs GAP-016).
 */
export interface CredentialStatusResult {
  credentialId: string;
  exchangeName: string;
  healthy: boolean;
  message: string;
  latencyMs: number;
  checkedAt: string;
}

/**
 * Symbol-sync status for a credential's exchange.
 * Mirrors backend CredentialSyncStatusResponse (exchange.rs GAP-016).
 */
export interface CredentialSyncStatusResult {
  credentialId: string;
  exchangeName: string;
  symbolCount: number;
  lastSyncedAt: string | null;
  everSynced: boolean;
}

export type ReconciliationVenue = "kraken" | "okx";

const GENERATED_RECONCILIATION_VENUES: Readonly<
  Record<ReconciliationVenue, GeneratedReconciliationVenue>
> = {
  kraken: GeneratedReconciliationVenue.Kraken,
  okx: GeneratedReconciliationVenue.Okx,
};

export type ReconciliationBindingUnavailableReason =
  | "credential_inactive"
  | "credential_read_disabled"
  | "credential_expired"
  | "venue_mismatch";

/** Sanitized tenant/venue account selection. No credential material is returned. */
export interface ReconciliationBinding {
  venue: ReconciliationVenue;
  credentialId: string;
  credentialLabel: string;
  exchangeAccountId: string | null;
  testnet: boolean;
  ready: boolean;
  unavailableReason: ReconciliationBindingUnavailableReason | null;
  updatedAt: string;
}

/** One active, readable, unexpired credential eligible for a venue binding. */
export interface ReconciliationBindingCandidate {
  venue: ReconciliationVenue;
  credentialId: string;
  credentialLabel: string;
  exchangeAccountId: string | null;
  testnet: boolean;
}

/**
 * Supported exchanges for dropdown
 * Aligned with backend validation in exchange.rs:92
 */
export const SUPPORTED_EXCHANGES = [
  { label: "Binance", value: "binance" },
  { label: "Coinbase", value: "coinbase" },
  { label: "Kraken", value: "kraken" },
  { label: "OKX", value: "okx" },
  { label: "Bybit", value: "bybit" },
  { label: "KuCoin", value: "kucoin" },
] as const;

/**
 * Exchange Management Service
 *
 * Provides API integration for managing exchange connections.
 * All methods return Observables and handle errors gracefully.
 *
 * API Base: /api/v1/exchanges/credentials
 *
 * @example
 * ```typescript
 * // Inject the service
 * private exchangeService = inject(ExchangeService);
 *
 * // List all exchanges
 * this.exchangeService.listExchanges().subscribe(exchanges => {
 *   this.logger.debug('Loaded exchanges', { exchanges });
 * });
 *
 * // Create new exchange
 * this.exchangeService.createExchange({
 *   exchange_name: 'binance',
 *   label: 'Main Trading Account',
 *   api_key: 'xxx',
 *   api_secret: 'yyy'
 * }).subscribe(exchange => {
 *   this.logger.info('Exchange created', { exchange });
 * });
 * ```
 */
@Injectable({ providedIn: "root" })
export class ExchangeService {
  private readonly exchangeApi = inject(GeneratedExchangeService);

  /** Cached exchanges for reactive updates */
  private readonly exchangesCache: WritableSignal<Exchange[]> = signal([]);

  /** Loading state for UI */
  private readonly loading: WritableSignal<boolean> = signal(false);

  /** Error state for UI */
  private readonly errorState: WritableSignal<string | null> = signal(null);

  /** Public signals */
  readonly exchanges = this.exchangesCache.asReadonly();
  readonly isLoading = this.loading.asReadonly();
  readonly errorMessage = this.errorState.asReadonly();

  /** Public observables */
  readonly exchanges$ = toObservable(this.exchanges);
  readonly isLoading$ = toObservable(this.isLoading);
  readonly errorMessage$ = toObservable(this.errorMessage);

  /**
   * List all configured exchanges
   * GET /api/v1/exchanges/credentials
   * Delegates to generated ExchangeApiService.listExchangeCredentials()
   */
  listExchanges(): Observable<Exchange[]> {
    this.loading.set(true);
    this.errorState.set(null);

    return this.exchangeApi.listExchangeCredentials().pipe(
      map((responses) =>
        responses.map((r: GeneratedExchangeCredentialsResponse) =>
          this.mapGeneratedResponseToExchange(r),
        ),
      ),
      tap((exchanges) => {
        this.exchangesCache.set(exchanges);
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        return this.handleError(error, "Failed to load exchanges");
      }),
    );
  }

  /**
   * Get exchange details by ID
   * GET /api/v1/exchanges/credentials/:id
   * Delegates to generated ExchangeApiService.getExchangeCredentials()
   */
  getExchange(id: string): Observable<Exchange> {
    return this.exchangeApi.getExchangeCredentials(id).pipe(
      map((response) => this.mapGeneratedResponseToExchange(response)),
      catchError((error) =>
        this.handleError(error, "Failed to load exchange details"),
      ),
    );
  }

  /**
   * Create new exchange credentials
   * POST /api/v1/exchanges/credentials
   * Delegates to generated ExchangeApiService.createExchangeCredentials()
   */
  createExchange(
    request: CreateExchangeCredentialsRequest,
  ): Observable<Exchange> {
    this.loading.set(true);
    this.errorState.set(null);

    // Map local request type to generated type
    const generatedRequest: GeneratedCreateCredentialsRequest = {
      api_key: request.api_key,
      api_secret: request.api_secret,
      exchange_name: request.exchange_name,
      label: request.label,
      can_trade: request.can_trade,
      can_withdraw: request.can_withdraw,
      exchange_account_id: request.exchange_account_id,
      expires_at: request.expires_at,
      passphrase: request.passphrase,
      rate_limit_per_minute: request.rate_limit_per_minute,
      rate_limit_per_second: request.rate_limit_per_second,
      subaccount: request.subaccount,
      testnet: request.testnet,
    };

    return this.exchangeApi.createExchangeCredentials(generatedRequest).pipe(
      map((response) => this.mapGeneratedResponseToExchange(response)),
      tap((exchange) => {
        this.exchangesCache.update((current) => [...current, exchange]);
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        return this.handleError(error, "Failed to create exchange");
      }),
    );
  }

  /**
   * Update existing exchange credentials
   * PUT /api/v1/exchanges/credentials/:id
   * Delegates to generated ExchangeApiService.updateExchangeCredentials()
   */
  updateExchange(
    id: string,
    request: UpdateExchangeCredentialsRequest,
  ): Observable<Exchange> {
    this.loading.set(true);
    this.errorState.set(null);

    // Map local request type to generated type
    const generatedRequest: GeneratedUpdateCredentialsRequest = {
      label: request.label,
      can_trade: request.can_trade,
      can_withdraw: request.can_withdraw,
      can_read: request.can_read,
      is_active: request.is_active,
      rate_limit_per_minute: request.rate_limit_per_minute,
      rate_limit_per_second: request.rate_limit_per_second,
    };

    return this.exchangeApi
      .updateExchangeCredentials(id, generatedRequest)
      .pipe(
        map((response) => this.mapGeneratedResponseToExchange(response)),
        tap((exchange) => {
          this.exchangesCache.update((current) =>
            current.map((e) => (e.id === id ? exchange : e)),
          );
          this.loading.set(false);
        }),
        catchError((error) => {
          this.loading.set(false);
          return this.handleError(error, "Failed to update exchange");
        }),
      );
  }

  /**
   * Delete exchange credentials
   * DELETE /api/v1/exchanges/credentials/:id
   * Delegates to generated ExchangeApiService.deleteExchangeCredentials()
   */
  deleteExchange(id: string): Observable<void> {
    this.loading.set(true);
    this.errorState.set(null);

    return this.exchangeApi.deleteExchangeCredentials(id).pipe(
      map(() => undefined as void),
      tap(() => {
        this.exchangesCache.update((current) =>
          current.filter((e) => e.id !== id),
        );
        this.loading.set(false);
      }),
      catchError((error) => {
        this.loading.set(false);
        return this.handleError(error, "Failed to delete exchange");
      }),
    );
  }

  /**
   * Test connection to exchange
   * POST /api/v1/exchanges/credentials/:id/test
   * Delegates to generated ExchangeApiService.testExchangeCredentials()
   */
  testConnection(id: string): Observable<ConnectionTestResult> {
    return this.exchangeApi.testExchangeCredentials(id).pipe(
      map((result) => result as ConnectionTestResult),
      tap((result) => {
        this.exchangesCache.update((current) =>
          current.map((e) => {
            if (e.id === id) {
              return {
                ...e,
                status: result.success
                  ? ("connected" as const)
                  : ("error" as const),
                lastUsedAt: new Date(),
              };
            }
            return e;
          }),
        );
      }),
      catchError((error) =>
        this.handleError(error, "Failed to test connection"),
      ),
    );
  }

  /**
   * Probe the live connection health of an exchange.
   * POST /api/v1/exchanges/:exchangeName/reconnect
   *
   * The backend performs a health check on the exchange's live connection and
   * reports its current status (and recovery is handled by the connection
   * infrastructure). This is the real per-exchange connection-status API used
   * by the WebSocket-connections health panel, which replaces the previous
   * per-exchange WebSocket probing that targeted /ws/exchange-<name> routes the
   * backend never serves.
   *
   * @param exchangeName Lowercase exchange identifier (e.g. 'binance').
   * Delegates to generated ExchangeApiService.reconnectExchange().
   */
  checkExchangeHealth(exchangeName: string): Observable<ExchangeHealthResult> {
    return this.exchangeApi.reconnectExchange(exchangeName).pipe(
      map((result: GeneratedReconnectExchangeResponse) => ({
        healthy: result.healthy,
        message: result.message,
        exchange: result.exchange,
        latencyMs: result.latencyMs,
        timestamp: result.timestamp,
      })),
      catchError((error) =>
        this.handleError(
          error,
          `Failed to check ${exchangeName} connection health`,
        ),
      ),
    );
  }

  /**
   * Mark credentials for rotation
   * POST /api/v1/exchanges/credentials/:id/rotate
   * Delegates to generated ExchangeApiService.rotateExchangeCredentials()
   */
  rotateCredentials(
    id: string,
  ): Observable<{ success: boolean; message: string }> {
    return this.exchangeApi.rotateExchangeCredentials(id).pipe(
      map((result) => result as { success: boolean; message: string }),
      tap(() => {
        this.exchangesCache.update((current) =>
          current.map((e) => {
            if (e.id === id) {
              return { ...e, rotationRequired: true };
            }
            return e;
          }),
        );
      }),
      catchError((error) =>
        this.handleError(error, "Failed to mark credentials for rotation"),
      ),
    );
  }

  /**
   * Get tradable markets (symbols) for a credential's exchange (GAP-016).
   * GET /api/v1/exchanges/credentials/:id/markets
   * Returns symbols already stored in the symbol catalogue for the credential's
   * exchange and tenant. Does NOT hit the live exchange — use triggerCredentialSync
   * to refresh from source.
   */
  getCredentialMarkets(id: string): Observable<CredentialMarketsResult> {
    return this.exchangeApi.getCredentialMarkets(id).pipe(
      map((r: GeneratedCredentialMarketsResponse) => ({
        credentialId: r.credentialId,
        exchangeName: r.exchangeName,
        symbols: r.symbols,
        count: r.count,
      })),
      catchError((error) =>
        this.handleError(error, "Failed to load credential markets"),
      ),
    );
  }

  /**
   * Get live connectivity/validity status for a credential's exchange (GAP-016).
   * GET /api/v1/exchanges/credentials/:id/status
   * Reads the exchange router's cached health metrics for the credential's venue.
   * Low-latency (no live exchange API call); reflects the continuously-updated
   * background health check.
   */
  getCredentialStatus(id: string): Observable<CredentialStatusResult> {
    return this.exchangeApi.getCredentialStatus(id).pipe(
      map((r: GeneratedCredentialStatusResponse) => ({
        credentialId: r.credentialId,
        exchangeName: r.exchangeName,
        healthy: r.healthy,
        message: r.message,
        latencyMs: r.latencyMs,
        checkedAt: r.checkedAt,
      })),
      catchError((error) =>
        this.handleError(error, "Failed to load credential status"),
      ),
    );
  }

  /**
   * Get symbol-sync status for a credential's exchange (GAP-016).
   * GET /api/v1/exchanges/credentials/:id/sync-status
   * Returns the count of symbols stored for the credential's exchange and tenant,
   * and the timestamp of the most recent sync. No live exchange call.
   */
  getCredentialSyncStatus(id: string): Observable<CredentialSyncStatusResult> {
    return this.exchangeApi.getCredentialSyncStatus(id).pipe(
      map((r: GeneratedCredentialSyncStatusResponse) => ({
        credentialId: r.credentialId,
        exchangeName: r.exchangeName,
        symbolCount: r.symbolCount,
        lastSyncedAt: r.lastSyncedAt ?? null,
        everSynced: r.everSynced,
      })),
      catchError((error) =>
        this.handleError(error, "Failed to load credential sync status"),
      ),
    );
  }

  /**
   * Trigger a symbol sync for a credential's exchange (GAP-016).
   * POST /api/v1/exchanges/credentials/:id/sync
   * Fetches tradable symbols from the credential's exchange and upserts them
   * into the tenant's symbol catalogue. Only Binance/Coinbase/Kraken are
   * supported by the live fetch layer; others return 400.
   */
  triggerCredentialSync(id: string): Observable<GeneratedSyncResponse> {
    return this.exchangeApi
      .triggerCredentialSync(id)
      .pipe(
        catchError((error) =>
          this.handleError(error, "Failed to trigger credential sync"),
        ),
      );
  }

  /** Load the tenant's explicit venue-to-credential reconciliation bindings. */
  listReconciliationBindings(): Observable<ReconciliationBinding[]> {
    return this.exchangeApi.listReconciliationBindings().pipe(
      map((responses) =>
        responses.map((response) => this.parseReconciliationBinding(response)),
      ),
      catchError((error) =>
        this.handleContractOrHttpError(
          error,
          "Failed to load reconciliation bindings",
        ),
      ),
    );
  }

  /** Load sanitized credentials that are currently eligible for binding. */
  listReconciliationBindingCandidates(): Observable<
    ReconciliationBindingCandidate[]
  > {
    return this.exchangeApi.listReconciliationBindingCandidates().pipe(
      map((responses) =>
        responses.map((response) =>
          this.parseReconciliationCandidate(response),
        ),
      ),
      catchError((error) =>
        this.handleContractOrHttpError(
          error,
          "Failed to load reconciliation candidates",
        ),
      ),
    );
  }

  /** Create or replace exactly one tenant/venue binding. */
  upsertReconciliationBinding(
    venue: ReconciliationVenue,
    credentialId: string,
  ): Observable<ReconciliationBinding> {
    const request: GeneratedUpsertReconciliationBindingRequest = {
      credential_id: credentialId,
    };
    return this.exchangeApi
      .upsertReconciliationBinding(
        GENERATED_RECONCILIATION_VENUES[venue],
        request,
      )
      .pipe(
        map((response) => this.parseReconciliationBinding(response)),
        catchError((error) =>
          this.handleContractOrHttpError(
            error,
            `Failed to save ${venue.toUpperCase()} reconciliation binding`,
          ),
        ),
      );
  }

  /** Remove a binding so future reconciliation attempts fail closed for it. */
  deleteReconciliationBinding(venue: ReconciliationVenue): Observable<void> {
    return this.exchangeApi
      .deleteReconciliationBinding(GENERATED_RECONCILIATION_VENUES[venue])
      .pipe(
        map(() => undefined),
        catchError((error) =>
          this.handleError(
            error,
            `Failed to remove ${venue.toUpperCase()} reconciliation binding`,
          ),
        ),
      );
  }

  /**
   * Refresh the cache by re-fetching from API
   */
  refreshCache(): Observable<Exchange[]> {
    return this.listExchanges();
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorState.set(null);
  }

  /**
   * Map generated API response to frontend Exchange model
   * Uses the generated ExchangeCredentialsResponse type from OpenAPI.
   *
   * NOTE: `status` is intentionally NOT derived from live connectivity here. The
   * credentials list endpoint only returns `is_active` (a credential-activation
   * flag), which says nothing about whether the venue is actually reachable. An
   * active credential is therefore mapped to `'unknown'` (health not yet probed)
   * rather than a fabricated `'connected'`; a disabled credential is genuinely
   * `'disconnected'`. Real connection health arrives via the per-exchange probe
   * endpoints (test / reconnect) and is folded into the store by
   * {@link ExchangeStore.applyHealthProbe}.
   */
  private mapGeneratedResponseToExchange(
    response: GeneratedExchangeCredentialsResponse,
  ): Exchange {
    return {
      id: response.id,
      name: response.exchange_name,
      label: response.label,
      status: response.is_active ? "unknown" : "disconnected",
      apiKeyMasked: this.maskCredential(response.label), // Label is shown, key is never exposed
      subaccount: response.subaccount ?? null,
      testnet: response.testnet,
      canTrade: response.can_trade,
      canWithdraw: response.can_withdraw,
      canRead: response.can_read,
      rateLimitPerSecond: response.rate_limit_per_second ?? null,
      rateLimitPerMinute: response.rate_limit_per_minute ?? null,
      lastUsedAt: response.last_used_at
        ? new Date(response.last_used_at)
        : null,
      rotationRequired: response.rotation_required,
      expiresAt: response.expires_at ? new Date(response.expires_at) : null,
      isActive: response.is_active,
      createdAt: new Date(response.created_at),
    };
  }

  private parseReconciliationBinding(
    response: GeneratedReconciliationBindingResponse,
  ): ReconciliationBinding {
    const venue = this.requireReconciliationVenue(
      response.venue,
      "binding venue",
    );
    const unavailableReason =
      response.unavailable_reason == null
        ? null
        : this.requireUnavailableReason(response.unavailable_reason);
    if (response.ready !== (unavailableReason === null)) {
      throw new Error(
        "Reconciliation binding readiness evidence is inconsistent",
      );
    }

    return {
      venue,
      credentialId: this.requireUuid(
        response.credential_id,
        "binding credential ID",
      ),
      credentialLabel: this.requireNonEmptyString(
        response.credential_label,
        "binding credential label",
      ),
      exchangeAccountId:
        response.exchange_account_id == null
          ? null
          : this.requireNonEmptyString(
              response.exchange_account_id,
              "binding exchange account ID",
            ),
      testnet: this.requireBoolean(response.testnet, "binding testnet flag"),
      ready: this.requireBoolean(response.ready, "binding readiness"),
      unavailableReason,
      updatedAt: this.requireTimestamp(
        response.updated_at,
        "binding updated timestamp",
      ),
    };
  }

  private parseReconciliationCandidate(
    response: GeneratedReconciliationBindingCandidateResponse,
  ): ReconciliationBindingCandidate {
    return {
      venue: this.requireReconciliationVenue(response.venue, "candidate venue"),
      credentialId: this.requireUuid(
        response.credential_id,
        "candidate credential ID",
      ),
      credentialLabel: this.requireNonEmptyString(
        response.credential_label,
        "candidate credential label",
      ),
      exchangeAccountId:
        response.exchange_account_id == null
          ? null
          : this.requireNonEmptyString(
              response.exchange_account_id,
              "candidate exchange account ID",
            ),
      testnet: this.requireBoolean(response.testnet, "candidate testnet flag"),
    };
  }

  private requireReconciliationVenue(
    value: unknown,
    field: string,
  ): ReconciliationVenue {
    if (value === "kraken" || value === "okx") {
      return value;
    }
    throw new Error(`Invalid ${field}`);
  }

  private requireUnavailableReason(
    value: unknown,
  ): ReconciliationBindingUnavailableReason {
    if (
      value === "credential_inactive" ||
      value === "credential_read_disabled" ||
      value === "credential_expired" ||
      value === "venue_mismatch"
    ) {
      return value;
    }
    throw new Error("Invalid reconciliation binding unavailable reason");
  }

  private requireUuid(value: unknown, field: string): string {
    if (
      typeof value === "string" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value,
      )
    ) {
      return value;
    }
    throw new Error(`Invalid ${field}`);
  }

  private requireNonEmptyString(value: unknown, field: string): string {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
    throw new Error(`Invalid ${field}`);
  }

  private requireBoolean(value: unknown, field: string): boolean {
    if (typeof value === "boolean") {
      return value;
    }
    throw new Error(`Invalid ${field}`);
  }

  private requireTimestamp(value: unknown, field: string): string {
    if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
        value,
      ) &&
      Number.isFinite(Date.parse(value))
    ) {
      return value;
    }
    throw new Error(`Invalid ${field}`);
  }

  private handleContractOrHttpError(
    error: unknown,
    defaultMessage: string,
  ): Observable<never> {
    if (error instanceof HttpErrorResponse) {
      return this.handleError(error, defaultMessage);
    }
    return throwError(() =>
      error instanceof Error ? error : new Error(defaultMessage),
    );
  }

  /**
   * Mask a credential string for display
   * Shows only last 4 characters
   */
  private maskCredential(value: string): string {
    if (!value || value.length < 4) {
      return "****";
    }
    return "•".repeat(Math.min(value.length - 4, 20)) + value.slice(-4);
  }

  /**
   * Handle HTTP errors with user-friendly messages
   */
  private handleError(
    error: HttpErrorResponse,
    defaultMessage: string,
  ): Observable<never> {
    // Error details are already captured in the response - no console logging needed
    // HTTP errors are automatically logged by Angular's HttpClient interceptors in development

    let errorMessage = defaultMessage;

    if (error.error instanceof ErrorEvent) {
      // Client-side error (network, etc.)
      errorMessage = `Network error: ${error.error.message}`;
    } else if (error.error?.message) {
      // Server returned error message
      errorMessage = error.error.message;
    } else if (error.status) {
      // HTTP error status codes
      switch (error.status) {
        case 400:
          errorMessage = "Invalid request. Please check your input.";
          break;
        case 401:
          errorMessage = "Authentication required. Please log in again.";
          break;
        case 403:
          errorMessage = "You do not have permission to perform this action.";
          break;
        case 404:
          errorMessage = "Exchange not found.";
          break;
        case 409:
          errorMessage = "Exchange with this name already exists.";
          break;
        case 422:
          errorMessage = "Validation failed. Please check your input.";
          break;
        case 429:
          errorMessage = "Rate limit exceeded. Please try again later.";
          break;
        case 500:
          errorMessage = "Server error. Please try again later.";
          break;
        case 503:
          errorMessage = "Service temporarily unavailable.";
          break;
        default:
          errorMessage = `Request failed with status ${error.status}`;
      }
    }

    this.errorState.set(errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}
